import {
  ButtonProps,
  IconButton,
  defineStyle,
  defineStyleConfig,
} from '@chakra-ui/react'

const defaultVariants = ['primary', 'secondary', 'tertiary']

const sizes = {
  md: defineStyle({
    height: 'auto',
    padding: '10px 24.5px',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 'normal',
  }),
  sm: defineStyle({
    height: 'auto',
    padding: '8px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 'normal',
  }),
}

const rounded = defineStyle({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '20px',
  cursor: 'pointer',
  svg: {
    color: 'presets.turquoise.100',
  },
  _hover: {
    bg: 'presets.neutral.20',
  },
  _active: {
    bg: 'presets.turquoise.10',
  },
})

const icon = defineStyle({
  border: '1px solid',
  borderColor: 'transparent',
  color: 'presets.neutral.60',
  fontSize: '14px',
  borderRadius: 'full',
  minWidth: 'unset',
  svg: {
    color: 'currentColor',
  },
  _dark: {
    color: 'white',
  },
  _focusVisible: {
    // borderColor: 'black',
  },
  _hover: {
    bgColor: 'presets.neutral.20',
    color: 'presets.turquoise.100',
  },
  _disabled: {
    opacity: 0.3,
  },
})

const primary = defineStyle({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 600,
  bgColor: 'presets.turquoise.60',
  color: 'presets.neutral.0',
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: '8px',
  borderColor: 'presets.turquoise.60',
  _hover: {
    bgColor: 'presets.turquoise.40',
    borderColor: 'presets.turquoise.40',
  },
  _focusVisible: {
    // borderColor: 'presets.neutral.100',
  },
  _disabled: {
    opacity: 0.3,
    '&:hover': {
      backgroundColor: 'presets.turquoise.60',
      borderColor: 'presets.turquoise.60',
    },
  },
  '&:disabled': {
    opacity: 0.3,
    _hover: {
      bgColor: 'presets.turquoise.60',
      borderColor: 'presets.turquoise.60',
    },
  },
} as ButtonProps)

const secondary = defineStyle({
  display: 'flex',
  padding: '6px',
  alignItems: 'center',
  flexShrink: 0,
  bgColor: 'transparent',
  color: 'presets.turquoise.100',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'presets.neutral.30',
  cursor: 'pointer',
  _hover: {
    borderColor: 'presets.turquoise.60',
  },
  _focusVisible: {
    // borderColor: 'presets.neutral.100',
  },
} as ButtonProps)

const tertiary = defineStyle({
  height: 'auto',
  margin: 0,
  padding: '4px',
  fontWeight: 600,
  color: 'presets.turquoise.70',
  background: 'transparent',
  borderColor: 'transparent',
  borderRadius: '4px',
  outline: 'none',
  cursor: 'pointer',
  _disabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  _dark: {
    color: 'presets.neutral.0',
    bg: 'transparent',
    _hover: {
      bg: 'transparent',
      color: 'presets.neutral.0',
    },
    _disabled: {
      color: 'presets.neutral.60',
      opacity: 0.8,
      cursor: 'not-allowed',
    },
  },
  _focusVisible: {
    // border: '1px solid',
    // borderColor: 'presets.neutral.100',
  },
} as ButtonProps)

const flexEndIconButton = defineStyle({
  color: 'presets.turquoise.100',
  ariaLabel: 'Back',
  width: '16px',
  mr: '8px',
  minWidth: 'unset',
  style: {
    justifyContent: 'flex-end',
  },
  _hover: { bg: 'transparent' },
} as ButtonProps)

const dangerButton = defineStyle({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  bgColor: 'presets.pink.70',
  color: 'presets.neutral.0',
  fontWeight: '600',
  textAlign: 'center',
  fontStyle: 'normal',
  cursor: 'pointer',
  borderRadius: '8px',
  borderColor: 'presets.turquoise.60',
  _hover: {
    bgColor: 'presets.pink.60',
  },
  _focusVisible: {
    bgColor: 'presets.pink.70',
    borderColor: 'presets.turquoise.100',
  },
} as ButtonProps)

const simple = defineStyle({
  display: 'flex',
  padding: '6px',
  alignItems: 'center',
  flexShrink: 0,
  bgColor: 'transparent',
  color: 'presets.turquoise.100',
  cursor: 'pointer',
  _hover: {
    color: 'presets.neutral.80',
  },
  _focusVisible: {
    borderColor: 'presets.neutral.100',
    borderRadius: '8px',
    border: '1px solid',
  },
} as ButtonProps)

const controlButton = defineStyle({
  bgColor: 'presets.neutral.0',
  color: 'presets.turquoise.100',
  border: '1px solid',
  borderColor: 'presets.neutral.30',
  borderRadius: '4px',
  fontWeight: '500',
  fontSize: '12px',
  _active: {
    bgColor: 'presets.turquoise.10',
  },
})

export const buttonTheme = defineStyleConfig({
  baseStyle: {
    boxSizing: 'border-box',
  },
  defaultProps: {
    variant: 'primary',
  },
  variants: {
    primary,
    secondary,
    tertiary,
    rounded,
    flexEndIconButton,
    dangerButton,
    simple,
    icon,
    controlButton,
  },
  sizes: {
    md: ({ variant }) => ({
      ...sizes.md,
      border: defaultVariants.includes(variant) ? '2px solid' : '',
    }),
    sm: ({ variant }) => ({
      ...sizes.sm,
      border: defaultVariants.includes(variant) ? '1px solid' : '',
    }),
  },
})

// React 19 removed defaultProps support for forwardRef components.
// Patch the render function to inject default props.
const _btnRef = IconButton as unknown as {
  render: (props: any, ref: any) => any
}
const _originalIconButtonRender = _btnRef.render
_btnRef.render = (props: any, ref: any) =>
  _originalIconButtonRender(
    { variant: 'icon', justifyContent: 'center', padding: '10px', ...props },
    ref,
  )
